// Localized data hooks: merge master English data with Kannada string overrides.
// Numeric fields (prices, kg, L, hrs, %) always come from the master data.

import { useMemo } from "react";
import { breeds as baseBreeds } from "@/data/breeds";
import { articles as baseArticles, learningTopics as baseLearningTopics } from "@/data/articles";
import { galleryImages as baseGallery, galleryCategories as baseGalleryCategories } from "@/data/gallery";
import { quizQuestions as baseQuizQuestions, quizIntro as baseQuizIntro, scoreQuiz } from "@/data/quiz";
import { useLang } from "./LanguageContext";
import { breedTranslationsKN } from "./breedTranslations";
import { articleTranslationsKN, galleryTranslationsKN, quizTranslationsKN } from "./contentTranslations";
import { translations } from "./translations";

function mergeBreedKN(breed) {
    const kn = breedTranslationsKN[breed.slug];
    if (!kn) return breed;
    return {
        ...breed,
        name: kn.name || breed.name,
        shortName: kn.shortName || breed.shortName,
        tagline: kn.tagline || breed.tagline,
        origin: kn.origin || breed.origin,
        classification: kn.classification || breed.classification,
        color: kn.color || breed.color,
        quickFacts: kn.quickFacts || breed.quickFacts,
        production: {
            ...breed.production,
            lactationPeriod: kn.production?.lactationPeriod || breed.production.lactationPeriod,
            suitableFor: kn.production?.suitableFor || breed.production.suitableFor,
        },
        housing: { ...breed.housing, ...(kn.housing || {}) },
        health: { ...breed.health, ...(kn.health || {}) },
        reproduction: { ...breed.reproduction, ...(kn.reproduction || {}) },
        environment: { ...breed.environment, ...(kn.environment || {}) },
        business: kn.business || breed.business,
        recommendation: { ...breed.recommendation, ...(kn.recommendation || {}) },
    };
}

export function useLocalizedBreeds() {
    const { lang } = useLang();
    return useMemo(() => {
        if (lang === "kn") return baseBreeds.map(mergeBreedKN);
        return baseBreeds;
    }, [lang]);
}

export function useLocalizedBreed(slug) {
    const breeds = useLocalizedBreeds();
    return breeds.find((b) => b.slug === slug);
}

export function useLocalizedArticles() {
    const { lang } = useLang();
    return useMemo(() => {
        if (lang !== "kn") return baseArticles;
        return baseArticles.map((a) => {
            const t = articleTranslationsKN[a.slug];
            return t ? { ...a, title: t.title, excerpt: t.excerpt, category: t.category } : a;
        });
    }, [lang]);
}

export function useLocalizedLearningTopics() {
    const { lang } = useLang();
    return useMemo(() => (lang === "kn" ? translations.kn.learningTopics : baseLearningTopics), [lang]);
}

export function useLocalizedGallery() {
    const { lang } = useLang();
    return useMemo(() => {
        if (lang !== "kn") return baseGallery;
        return baseGallery.map((g) => {
            const t = galleryTranslationsKN[g.id];
            return t ? { ...g, caption: t.caption, category: t.category } : g;
        });
    }, [lang]);
}

export function useLocalizedGalleryCategories() {
    const { lang } = useLang();
    return useMemo(
        () => (lang === "kn" ? translations.kn.gallery.categories : baseGalleryCategories),
        [lang]
    );
}

export function useLocalizedKnowledgeCategories() {
    const { lang } = useLang();
    return useMemo(
        () => (lang === "kn" ? translations.kn.knowledge.categories : translations.en.knowledge.categories),
        [lang]
    );
}

export function useLocalizedQuizQuestions() {
    const { lang } = useLang();
    return useMemo(() => {
        if (lang !== "kn") return baseQuizQuestions;
        return baseQuizQuestions.map((q) => {
            const kn = quizTranslationsKN[q.id];
            if (!kn) return q;
            return {
                ...q,
                title: kn.title,
                subtitle: kn.subtitle,
                options: q.options.map((o) => {
                    const oo = kn.options?.[o.id];
                    return oo ? { ...o, label: oo.label, desc: oo.desc } : o;
                }),
            };
        });
    }, [lang]);
}

export function useLocalizedQuizIntro() {
    const { lang } = useLang();
    return useMemo(() => {
        if (lang !== "kn") return baseQuizIntro;
        return {
            title: translations.kn.quiz.introTitle,
            subtitle: translations.kn.quiz.introSub,
        };
    }, [lang]);
}

export { scoreQuiz };
